import { OptimizationResultsWithStats } from '../types';

const HISTORY_STORAGE_KEY = 'optimization_results_history';
const MAX_HISTORY_ENTRIES = 50; // Maximum number of results to store

export interface HistoryEntry {
  id: string;
  timestamp: string;
  results: OptimizationResultsWithStats;
  parameters: {
    taskCount: number;
    iterations: number;
    initialPopulation: number;
    numberOfRuns?: number;
  };
}

/**
 * Stores optimization results in localStorage
 */
export function saveResultsToHistory(results: OptimizationResultsWithStats): string {
  const history = getHistory();
  
  const entry: HistoryEntry = {
    id: `result_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: results.timestamp || new Date().toISOString(),
    results,
    parameters: {
      taskCount: results.configuration.taskCount,
      iterations: results.configuration.initialPopulation, // Note: We don't store iterations separately, using initialPopulation as placeholder
      initialPopulation: results.configuration.initialPopulation,
      numberOfRuns: results.runNumber,
    },
  };

  history.unshift(entry); // Add to beginning
  
  // Keep only the most recent entries
  if (history.length > MAX_HISTORY_ENTRIES) {
    history.splice(MAX_HISTORY_ENTRIES);
  }

  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    return entry.id;
  } catch (error) {
    console.error('Failed to save results to history:', error);
    throw error;
  }
}

/**
 * Retrieves all results from history
 */
export function getHistory(): HistoryEntry[] {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!stored) return [];
    
    const history = JSON.parse(stored) as HistoryEntry[];
    return history;
  } catch (error) {
    console.error('Failed to load history:', error);
    return [];
  }
}

/**
 * Retrieves a specific result by ID
 */
export function getHistoryEntry(id: string): HistoryEntry | null {
  const history = getHistory();
  return history.find(entry => entry.id === id) || null;
}

/**
 * Removes a specific result from history
 */
export function removeHistoryEntry(id: string): void {
  const history = getHistory();
  const filtered = history.filter(entry => entry.id !== id);
  
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Failed to remove history entry:', error);
  }
}

/**
 * Clears all history
 */
export function clearHistory(): void {
  try {
    localStorage.removeItem(HISTORY_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear history:', error);
  }
}

/**
 * Gets history entry count
 */
export function getHistoryCount(): number {
  return getHistory().length;
}

